/**
 * [TD-47.6a-ui Gap A.2 + v1.1 Redline 5] CompactActionPanel - Zone 1 (~22%
 * of center column under v1.1).
 *
 * 2×2 grid + center bid row:
 *   TOP-LEFT:    photo (Sleeper CDN) + name/pos pill + team · bye · nom-by
 *   TOP-RIGHT:   live mini-stats (PPG / ADP rank / Bye)
 *   CENTER:      $current_bid (large mono) · [-] $bid [+]  · [BID $X] button
 *   BOTTOM-LEFT: clock (mm:ss)
 *   BOTTOM-RIGHT: Going Once / Going Twice / Winning pill
 *
 * Strict `overflow: hidden` + `min-w-0` boundaries on every flex child to
 * kill the v1.0 text-overflow bug at narrow viewports.
 *
 * Skipped from ActiveNomination by design (paid-tier polish, TD-47.6b):
 *   • Confetti
 *   • Get-ready countdown ceremony (server-authoritative isPreBidding still
 *     hides bid controls)
 *   • SOLD slam overlay (handled at parent layer)
 */
import { useEffect, useRef, useState } from "react";
import { useDraft } from "../../contexts/DraftContext";
import { useAuth } from "../../contexts/AuthContext";
import PositionBadge from "../PositionBadge";
import ManagerName from "../ManagerName";
import { Plus, User as UserIcon } from "lucide-react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import { BidConfidenceLive } from "../analytics/BidConfidenceLive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useTimer(timerRemaining, timerVersion) {
  const [timeLeft, setTimeLeft] = useState(timerRemaining || 0);
  const trRef = useRef(timerRemaining);
  trRef.current = timerRemaining;
  useEffect(() => {
    setTimeLeft(trRef.current || 0);
    const id = setInterval(() => {
      setTimeLeft((t) => (t > 0 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [timerVersion]);
  return timeLeft;
}

/**
 * Live-fetched player metrics for the mini-stat block (top-right) + photo
 * (top-left). Keyed on nominated player UUID. Single-flight per player.
 */
function usePlayerSnapshot(playerId, leagueId) {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    if (!playerId || !leagueId) { setSnap(null); return; }
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/players/${playerId}/metrics`, {
          params: { league_id: leagueId },
          headers: authHeaders(),
        });
        if (alive) setSnap(res.data);
      } catch {
        if (alive) setSnap(null);
      }
    })();
    return () => { alive = false; };
  }, [playerId, leagueId]);
  return snap;
}

function PlayerPhoto({ url, position }) {
  const [errored, setErrored] = useState(false);
  if (!url || errored) {
    return (
      <div
        className="w-32 h-32 rounded-2xl bg-[#252525] border border-[#333] flex items-center justify-center shrink-0"
        data-testid="compact-action-photo-fallback"
      >
        <UserIcon size={56} className="text-[#666]" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={position || "Player"}
      onError={() => setErrored(true)}
      className="w-32 h-32 rounded-2xl object-cover border border-[#333] shrink-0 bg-[#252525]"
      data-testid="compact-action-photo"
    />
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div className="flex flex-col items-end min-w-0" data-testid={testid}>
      <p className="font-mono font-black text-base text-[#F5F5F5] leading-none">
        {value === null || value === undefined ? "-" : value}
      </p>
      <p className="font-display font-bold text-[9px] uppercase tracking-wider text-[#A0A0A0] mt-0.5">
        {label}
      </p>
    </div>
  );
}

export default function CompactActionPanel() {
  const { draftState, nomination, soldInfo, placeBid, leagueId } = useDraft();
  const { user } = useAuth();
  const myId = user?.id;

  const [bidAmount, setBidAmount] = useState(1);
  const [bidError, setBidError] = useState("");

  const currentBid = nomination?.current_bid || 1;
  useEffect(() => {
    if (nomination) setBidAmount(currentBid + 1);
  }, [currentBid, nomination?.id]);

  const isHighestBidder = nomination?.current_bidder_id === myId;
  const state = draftState?.state;
  const isPreBidding = state === "PRE_BIDDING";
  const isPaused = state === "PAUSED";
  const isComplete = state === "DRAFT_COMPLETE";
  const isLobby = state === "LOBBY";
  const isSold = !!soldInfo;
  const currentNominatorId = draftState?.current_nominator_id;
  const isMyTurnToNominate = currentNominatorId === myId;

  const timeLeft = useTimer(
    draftState?.timer_remaining,
    draftState?.timer_version || 0,
  );

  const handleBid = () => {
    if (bidAmount <= currentBid) {
      setBidError(`Must be more than $${currentBid}`);
      return;
    }
    setBidError("");
    placeBid(bidAmount);
  };

  const timerColor =
    timeLeft <= 5 ? "text-[#FF6B00]" : timeLeft <= 10 ? "text-[#FFB870]" : "text-[#F5F5F5]";
  const mmss = `${String(Math.floor(timeLeft / 60)).padStart(2, "0")}:${String(timeLeft % 60).padStart(2, "0")}`;

  // Live player snapshot - keyed on nominated player.
  const snap = usePlayerSnapshot(nomination?.player?.id, leagueId);
  const photoUrl = snap?.player_info?.photo_url || null;
  const scoringFmt = snap?.scoring_format_used || "half_ppr";
  const ppgVal = snap?.metrics?.[`pts_per_game_${scoringFmt}`];
  const adpRankVal = snap?.metrics?.[`adp_rank_${scoringFmt}`];

  // ── Empty states ─────────────────────────────────────────────────────
  if (isLobby || isComplete || isPaused) {
    const label = isLobby ? "Waiting for commissioner…" : isComplete ? "Draft complete" : "Draft paused";
    const color = isComplete ? "text-[#00C853]" : isPaused ? "text-yellow-400" : "text-[#A0A0A0]";
    return (
      <div
        className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex items-center justify-center h-full px-4 overflow-hidden"
        data-testid="compact-action-panel"
      >
        <p className={`font-display font-black text-base uppercase tracking-wider ${color}`}>
          {label}
        </p>
      </div>
    );
  }

  // ── Waiting for nominator (no live nom yet) ──────────────────────────
  if (!nomination && state === "NOMINATION") {
    return (
      <div
        className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex items-center justify-between px-4 h-full overflow-hidden"
        data-testid="compact-action-panel"
      >
        <div className="min-w-0">
          <p className="font-display font-bold text-[10px] uppercase tracking-wider text-[#FF6B00]">
            On the Clock
          </p>
          <p className="font-display font-black text-lg uppercase text-[#F5F5F5] leading-tight mt-0.5 truncate">
            {isMyTurnToNominate
              ? "Your turn to nominate"
              : draftState?.managers?.[currentNominatorId]?.team_name || "Waiting…"}
          </p>
        </div>
        <div className={`font-mono font-black text-3xl ${timerColor} shrink-0 ml-3`}>{mmss}</div>
      </div>
    );
  }

  // ── Active or pre-bidding nomination ────────────────────────────────
  if (!nomination) return null;
  const p = nomination.player;
  const nominatorName =
    draftState?.managers?.[nomination.nominated_by]?.team_name || "Unknown";
  const isBot = !!draftState?.managers?.[nomination.nominated_by]?.is_bot;
  const goingState =
    state === "GOING_ONCE" ? "Going Once"
      : state === "GOING_TWICE" ? "Going Twice"
      : null;

  // [TD-47.6a-ui v1.3 Redline 2] State-conditional layout.
  //   • State A "Showcase"   - PRE_BIDDING (10s get-ready) + SOLD celebration.
  //                            Photo + identity centered + large typography,
  //                            NO bid controls. Timer (or SOLD line) bottom-left.
  //   • State B "Bidding"    - BIDDING / GOING_ONCE / GOING_TWICE. v1.2 layout
  //                            with 2×2 grid + bid controls dominant.
  const showcase = isPreBidding || isSold;
  if (showcase) {
    return (
      <ShowcaseLayout
        player={p}
        photoUrl={photoUrl}
        nominatorName={nominatorName}
        isBot={isBot}
        ppgVal={ppgVal}
        adpRankVal={adpRankVal}
        mmss={mmss}
        timerColor={timerColor}
        isSold={isSold}
        soldInfo={soldInfo}
      />
    );
  }

  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid="compact-action-panel"
      data-state-mode="bidding"
    >
      {/* TOP ROW - player ID (left) | mini-stats (right) */}
      <div className="px-3 pt-2.5 pb-1.5 flex items-start justify-between gap-3 shrink-0 min-w-0">
        {/* TOP-LEFT - photo + name + sub-line */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1 overflow-hidden">
          <PlayerPhoto url={photoUrl} position={p?.position} />
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-1.5 min-w-0">
              <PositionBadge position={p?.position} size="sm" />
              <p
                className="font-display font-black text-base uppercase text-[#F5F5F5] truncate leading-tight"
                data-testid="compact-action-player-name"
              >
                {p?.name}
              </p>
            </div>
            <p className="font-body text-[10px] text-[#A0A0A0] mt-0.5 truncate">
              {p?.nfl_team} · Bye {p?.bye_week} · Nom by{" "}
              <ManagerName name={nominatorName} isBot={isBot} iconSize={9} testId="compact-nom-by" />
            </p>
          </div>
        </div>
        {/* TOP-RIGHT - mini-stats */}
        <div className="flex items-start gap-3 shrink-0">
          <MiniStat label="PPG" value={ppgVal} testid="compact-action-stat-ppg" />
          <MiniStat
            label="ADP RK"
            value={adpRankVal ? `${p?.position}${adpRankVal}` : null}
            testid="compact-action-stat-adp"
          />
        </div>
      </div>

      {/* CENTER - bid row (visual protagonist) */}
      <div className="flex-1 min-h-0 px-3 py-1.5 flex items-center justify-center gap-4 overflow-hidden">
        {/* Current bid */}
        <div className="shrink-0 text-center">
          <p className="font-body text-[9px] uppercase tracking-wider text-[#A0A0A0]">
            Current bid
          </p>
          <p
            className="font-mono font-black text-3xl text-[#F5F5F5] leading-none mt-0.5"
            data-testid="current-bid"
          >
            ${currentBid}
          </p>
          {nomination.current_bidder_id ? (
            <p
              className={`font-body text-[9px] mt-0.5 ${
                isHighestBidder ? "text-[#00C853]" : "text-[#A0A0A0]"
              }`}
            >
              {isHighestBidder
                ? "You're winning!"
                : `${draftState?.managers?.[nomination.current_bidder_id]?.team_name || "..."} leads`}
            </p>
          ) : null}
        </div>

        {/* Bid input + paddle (BIDDING / GOING_ONCE / GOING_TWICE only) */}
        {!isHighestBidder ? (
          <div className="flex items-center gap-2 shrink-0">
            <div className="bg-[#252525] rounded-lg px-3 py-1.5 min-w-[64px] text-center">
              <span
                className="font-mono font-black text-lg text-[#F5F5F5]"
                data-testid="bid-amount-display"
              >
                ${bidAmount}
              </span>
            </div>
            <button
              onClick={() => setBidAmount((b) => b + 1)}
              className="w-8 h-8 bg-[#252525] hover:bg-[#2E2E2E] rounded-lg text-[#F5F5F5] flex items-center justify-center transition-colors"
              data-testid="bid-amount-increment"
            >
              <Plus size={14} />
            </button>
            <button
              data-testid="bid-action-btn"
              onClick={handleBid}
              className="bg-[#00C853] hover:bg-[#00E676] text-[#111111] font-display font-black uppercase tracking-wider text-xl px-8 py-4 rounded-lg transition-all active:scale-95"
              style={{ boxShadow: "0 0 20px rgba(0,200,83,0.35)" }}
            >
              Bid ${bidAmount}
            </button>
          </div>
        ) : (
          <div
            className="bg-[#00C853]/10 border border-[#00C853]/40 text-[#00C853] font-display font-bold uppercase text-xs px-3 py-1.5 rounded-lg shrink-0"
            data-testid="winning-pill"
          >
            Winning ${currentBid}
          </div>
        )}
      </div>

      {/* [v1.6] Bottom row unified - big clock LEFT · compact Bid Confidence
          CENTER · going-once/twice pill or bid error RIGHT. All on one line
          so the BID button above stays unobstructed. */}
      <div className="px-3 pb-2 pt-1 flex items-center justify-between gap-3 shrink-0 min-w-0">
        <div
          className={`font-mono font-black text-4xl ${timerColor} shrink-0 tabular-nums`}
          data-testid="bid-timer"
        >
          {mmss}
        </div>
        <div className="flex-1 min-w-0 flex justify-center">
          <BidConfidenceLive
            leagueId={leagueId}
            nomination={nomination}
            currentBid={currentBid}
            userBudget={draftState?.managers?.[myId]?.budget}
            userRosterFilled={(draftState?.managers?.[myId]?.roster || []).length}
            rosterSize={draftState?.roster_size || 14}
          />
        </div>
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          {goingState ? (
            <span className="font-display font-black text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#FF6B00]/20 text-[#FF6B00] shrink-0">
              {goingState}
            </span>
          ) : null}
          {bidError ? (
            <p className="text-[#FF4444] font-body text-[10px] truncate">{bidError}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * [TD-47.6a-ui v1.3 Redline 2] State A "Showcase" - anticipation + celebration.
 *
 * Used for engine states:
 *   • PRE_BIDDING - 10s get-ready window after a fresh nomination
 *   • SOLD        - celebration moment (soldInfo non-null; engine clears on
 *                   NOMINATION_READY, typically ~3-5s)
 *
 * Layout:
 *   • Photo (128px) + identity centered horizontally
 *   • Large player-name typography (text-3xl uppercase)
 *   • NO bid controls
 *   • Bottom-left: timer during PRE_BIDDING, "SOLD to {team} for ${amount}"
 *     during SOLD (orange #FF6B00 accent, bold weight - founder Q3 default)
 */
function ShowcaseLayout({
  player, photoUrl, nominatorName, isBot,
  ppgVal, adpRankVal, mmss, timerColor, isSold, soldInfo,
}) {
  const p = player || {};
  return (
    <div
      className="bg-[#1C1C1C] border border-[#252525] rounded-2xl flex flex-col h-full overflow-hidden"
      data-testid="compact-action-panel"
      data-state-mode={isSold ? "showcase-sold" : "showcase-prebid"}
    >
      {/* CENTERED SHOWCASE BODY */}
      <div className="flex-1 min-h-0 px-4 py-3 flex items-center justify-center gap-5 overflow-hidden">
        <PlayerPhoto url={photoUrl} position={p?.position} />
        <div className="min-w-0 flex-1 max-w-[60%]">
          <div className="flex items-center gap-2 min-w-0">
            <PositionBadge position={p?.position} size="sm" />
            <p
              className="font-display font-black text-3xl uppercase text-[#F5F5F5] truncate leading-tight"
              data-testid="showcase-player-name"
            >
              {p?.name}
            </p>
          </div>
          <p className="font-body text-xs text-[#A0A0A0] mt-1 truncate">
            {p?.nfl_team} · Bye {p?.bye_week} · Nom by{" "}
            <ManagerName name={nominatorName} isBot={isBot} iconSize={11} testId="showcase-nom-by" />
          </p>
          <div className="flex items-baseline gap-5 mt-2">
            <MiniStat label="PPG" value={ppgVal} testid="showcase-stat-ppg" />
            <MiniStat
              label="ADP RK"
              value={adpRankVal ? `${p?.position}${adpRankVal}` : null}
              testid="showcase-stat-adp"
            />
          </div>
        </div>
      </div>

      {/* BOTTOM-LEFT - timer (PRE_BIDDING) or SOLD line (founder Q3 default treatment) */}
      <div className="px-4 pb-3 pt-1 shrink-0 min-w-0">
        {isSold && soldInfo ? (
          <p
            className="font-display font-black text-base uppercase tracking-wider text-[#FF6B00] truncate"
            data-testid="showcase-sold-line"
          >
            SOLD to {soldInfo.winner_name || "-"} for ${soldInfo.winning_bid ?? "?"}
          </p>
        ) : (
          <div
            className={`font-mono font-black text-2xl ${timerColor} shrink-0`}
            data-testid="showcase-timer"
          >
            {mmss}
          </div>
        )}
      </div>
    </div>
  );
}
